export interface AccountRole {
  id?: string | number | null;
  name?: string | null;
  code?: string | null;
  roleName?: string | null;
  department_id?: string;
  department?: { id?: string; name?: string } | string | null;
}

export interface AccountWithRoles {
  role?: AccountRole | string | null;
  roles?: AccountRole[];
  role_id?: string | number | null;
  role_ids?: Array<string | number>;
  access?: string;
}

/** A supplied role set is authoritative, including an empty set after revocation. */
export function getAccountRoles(account: AccountWithRoles | null | undefined): AccountRole[] {
  if (Array.isArray(account?.roles)) return account.roles;
  return account?.role && typeof account.role === 'object' ? [account.role] : [];
}

export function getAccountRoleIds(account: AccountWithRoles | null | undefined): string[] {
  const ids = Array.isArray(account?.role_ids)
    ? account.role_ids
    : Array.isArray(account?.roles)
      ? account.roles.map((role) => role.id)
      : account?.role_id != null
        ? [account.role_id]
        : getAccountRoles(account).map((role) => role.id);
  return [...new Set(ids.filter((id) => id != null && String(id) !== '').map(String))].sort();
}

export function selectPrimaryRoleId(roleIds: string[], previousPrimary?: string | number | null) {
  const ids = [...new Set(roleIds.map(String))].sort();
  const previous = previousPrimary == null ? '' : String(previousPrimary);
  return ids.includes(previous) ? previous : ids[0];
}

/** Server role assignments are ordered with the current primary role first. */
export function getProjectedPrimaryRoleId(
  assignments: Array<{ roleId: string }>,
  roleIds: string[],
  previousPrimary?: string | number | null,
) {
  return assignments[0]?.roleId ?? selectPrimaryRoleId(roleIds, previousPrimary);
}

export function isAccountAdmin(account: AccountWithRoles | null | undefined): boolean {
  const hasAdminRole = getAccountRoles(account).some((role) =>
    [role.name, role.code].some((value) => value?.trim().toLowerCase() === 'admin'),
  );
  // Legacy access is only a fallback for responses that do not expose the complete role set.
  return hasAdminRole || (!Array.isArray(account?.roles) && account?.access === 'admin');
}

export function getAccountRoleNames(account: AccountWithRoles | null | undefined): string {
  return getAccountRoles(account)
    .map((role) => role.name || role.code || String(role.id))
    .join('、');
}

export function getAccountDepartmentNames(account: AccountWithRoles | null | undefined): string {
  return [
    ...new Set(
      getAccountRoles(account)
        .map((role) =>
          typeof role.department === 'object' ? role.department?.name : role.department,
        )
        .filter(Boolean),
    ),
  ].join('、');
}
